import React from 'react';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import { amountDisplay, isDarkModeOn } from '../../util/helper';
import { useLanguage } from '../../context/LanguageContext';

const LineChart = ({ monthlyTotalBalance, months, performanceMode = {} }) => {
    const { language, t } = useLanguage();
    const isCompact = Boolean(performanceMode.compact);
    const disableAnimation = Boolean(performanceMode.disableAnimation);
    const disableEffects = Boolean(performanceMode.disableEffects);
    const chartHeight = isCompact ? 250 : 380;
    const categories = months.map((date) => dayjs(date).format('MMM'));
    const isDark = isDarkModeOn();
    const labelColor = isDark ? '#9ba3aa' : '#958f9f';
    const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(149,143,159,0.18)';

    const series = [
        {
            name: t('balance'),
            data: Object.values(monthlyTotalBalance),
        },
    ];

    const options = {
        chart: {
            type: 'area',
            height: 380,
            fontFamily: 'Lato, sans-serif',
            background: 'transparent',
            toolbar: { show: false },
            zoom: { enabled: false },
            animations: {
                enabled: !disableAnimation,
                easing: 'easeinout',
                speed: disableAnimation ? 1 : 450,
            },
            dropShadow: {
                enabled: !disableEffects,
                top: 6,
                left: 0,
                blur: 8,
                opacity: 0.18,
                color: '#06BFFA',
            },
        },
        colors: ['#06BFFA'],
        dataLabels: { enabled: false },
        stroke: {
            curve: 'smooth',
            width: 2.5,
            lineCap: 'round',
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: isDark ? 0.35 : 0.28,
                opacityTo: 0.02,
                stops: [0, 90, 100],
            },
        },
        xaxis: {
            categories,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                style: {
                    fontSize: '11px',
                    colors: labelColor,
                },
            },
            tooltip: { enabled: false },
        },
        yaxis: {
            labels: {
                formatter: (val) => `${amountDisplay(val, language)}`,
                style: {
                    fontSize: '10px',
                    colors: labelColor,
                },
            },
            tickAmount: 4,
        },
        grid: {
            strokeDashArray: 4,
            borderColor: gridColor,
            padding: { left: 6, right: 6 },
        },
        tooltip: {
            theme: isDark ? 'dark' : 'light',
            y: {
                formatter: (val) => `৳${amountDisplay(val.toFixed(2), language)}`,
            },
            marker: { show: false },
        },
        markers: {
            size: 0,
            strokeColors: '#fff',
            strokeWidth: 2,
            hover: { size: disableEffects ? 3 : 5, sizeOffset: 2 },
        },
        responsive: [
            {
                breakpoint: 992,
                options: {
                    chart: { height: 320 },
                },
            },
            {
                breakpoint: 768,
                options: {
                    chart: { height: 260 },
                    stroke: { width: 2 },
                    xaxis: {
                        labels: { style: { fontSize: '9px' } },
                    },
                    yaxis: {
                        tickAmount: 4,
                        labels: {
                            formatter: (val) => `${amountDisplay(val, language)}`,
                            style: { fontSize: '9px' },
                        },
                    },
                    grid: { padding: { left: 2, right: 2 } },
                },
            },
            {
                breakpoint: 576,
                options: {
                    chart: { height: 235 },
                    xaxis: {
                        labels: { style: { fontSize: '8px' } },
                    },
                    yaxis: {
                        tickAmount: 3,
                        labels: {
                            formatter: (val) => `${amountDisplay(val, language)}`,
                            style: { fontSize: '8px' },
                        },
                    },
                },
            },
        ],
    };

    return (
        <section>
            <div className="custom-container">
                <div className="chart">
                    <div className="d-flex justify-content-between">
                        <h3 className="fw-semibold dark-text">
                            {t('balance')} |{' '}
                            <span className="light-text fw-normal">{`${categories[0]} - ${categories[categories.length - 1]}`}</span>
                        </h3>
                        <ul className="listing">
                            <li>W</li>
                            <li>M</li>
                            <li className="active">Y</li>
                        </ul>
                    </div>
                    <Chart options={options} series={series} type="area" height={chartHeight} />
                </div>
            </div>
        </section>
    );
};

export default LineChart;
